import { motion, useAnimation } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

// Define the type for React elements to avoid JSX namespace error
type ReactElement = React.ReactElement;

type DemoStep = {
  id: number;
  title: string;
  description: string;
  icon: ReactElement;
};

const demoSteps: DemoStep[] = [
  {
    id: 1,
    title: 'Objekt scannen',
    description: 'Richten Sie Ihren IHoloTool auf das gewünschte Objekt und drücken Sie den Scan-Knopf.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    id: 2,
    title: 'Quantenkompression',
    description: 'Ihr Objekt wird in Echtzeit komprimiert und sicher in der Cloud gespeichert.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
  },
  {
    id: 3,
    title: 'Hologramm projizieren',
    description: 'Wählen Sie einen Zielort und projizieren Sie das Hologramm in voller Größe.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15c-1.71 0-3.356.32-4.885.907M4.5 19.5l2.883-2.883c.378-.378.586-.88.586-1.414V10.5" />
      </svg>
    ),
  },
  {
    id: 4,
    title: 'Interagieren & Teilen',
    description: 'Bearbeiten Sie das Hologramm in Echtzeit und teilen Sie es mit Kollegen weltweit.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
    ),
  },
];

const Demo = () => {
  const [activeStep, setActiveStep] = useState(1);
  const controls = useAnimation();
  const demoRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  // Animation for the demo device
  const deviceAnimation = {
    initial: { opacity: 0, y: 50 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
    },
  };

  // Handle scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          startDemoSequence();
        } else {
          setIsInView(false);
        }
      },
      { threshold: 0.1 }
    );

    if (demoRef.current) {
      observer.observe(demoRef.current);
    }

    return () => {
      if (demoRef.current) {
        observer.unobserve(demoRef.current);
      }
    };
  }, []);

  // Start the demo sequence when component mounts or comes into view
  useEffect(() => {
    if (isInView) {
      startDemoSequence();
    } else {
      // Reset to first step when out of view
      setActiveStep(1);
    }
  }, [isInView]);

  const startDemoSequence = async () => {
    // Reset to first step
    setActiveStep(1);
    
    // Animate through each step
    for (let i = 1; i <= demoSteps.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds per step
      if (!isInView) break; // Stop if scrolled away
      
      setActiveStep(i < demoSteps.length ? i + 1 : 1); // Loop back to first step
      
      // Add a subtle animation to the device
      await controls.start({
        y: [0, -10, 0],
        transition: { duration: 0.5 }
      });
    }
  };

  // Get the current step
  const currentStep = demoSteps.find(step => step.id === activeStep) || demoSteps[0];

  return (
    <section id="demo" ref={demoRef} className="py-20 bg-gray-900 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/3 -left-20 w-96 h-96 rounded-full bg-blue-600 filter blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 rounded-full bg-purple-600 filter blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Erleben Sie die{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400">
              Zukunft der Holografie
            </span>
          </h2>
          <p className="text-lg text-gray-300">
            Sehen Sie, wie einfach es ist, mit IHoloTool Objekte zu scannen und als Hologramme zu projizieren.
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          {/* Demo steps */}
          <div className="w-full lg:w-1/2 space-y-6">
            {demoSteps.map((step) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-20% 0px -20% 0px" }}
                transition={{ duration: 0.5, delay: step.id * 0.1 }}
                className={`p-6 rounded-2xl transition-all duration-300 ${
                  activeStep === step.id 
                    ? 'bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 shadow-lg shadow-primary/10' 
                    : 'bg-white/5 border border-white/5 hover:border-white/10'
                }`}
                onMouseEnter={() => setActiveStep(step.id)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                    activeStep === step.id 
                      ? 'bg-primary/10 text-primary' 
                      : 'bg-white/5 text-gray-400'
                  }`}>
                    {step.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold ${
                      activeStep === step.id ? 'text-white' : 'text-gray-300'
                    }`}>
                      {step.title}
                    </h3>
                    <p className={`mt-1 ${
                      activeStep === step.id ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      {step.description}
                    </p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 ${
                    activeStep === step.id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-gray-600'
                  } flex items-center justify-center`}>
                    {activeStep === step.id && (
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Demo device */}
          <motion.div 
            className="w-full lg:w-1/2 relative"
            animate={controls}
            variants={deviceAnimation}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <div className="relative z-10 mx-auto max-w-md">
              {/* Device frame */}
              <div className="relative bg-gray-800 rounded-3xl p-2 shadow-2xl border border-gray-700 overflow-hidden">
                {/* Screen content */}
                <div className="relative aspect-[9/16] bg-gray-900 rounded-2xl overflow-hidden flex items-center justify-center">
                  {/* Animated content based on active step */}
                  <div className="text-center p-6">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary/20 to-transparent border border-primary/30 flex items-center justify-center">
                      {currentStep.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {currentStep.title}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {currentStep.description}
                    </p>
                    
                    {/* Loading/progress indicator */}
                    <div className="mt-8 w-full bg-gray-800 rounded-full h-2.5">
                      <motion.div 
                        className="h-2.5 rounded-full bg-gradient-to-r from-primary to-purple-600"
                        initial={{ width: '0%' }}
                        animate={{ width: '100%' }}
                        transition={{ 
                          duration: 3, 
                          repeat: Infinity,
                          repeatType: 'loop',
                          ease: 'linear'
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Device notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-gray-800 rounded-b-2xl z-10"></div>
                
                {/* Home button */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gray-700 rounded-full"></div>
              </div>
              
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/30 to-purple-600/30 rounded-3xl blur-3xl opacity-50 -z-10"></div>
            </div>
            
            {/* Floating elements */}
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-blue-500/20 blur-3xl -z-10"></div>
            <div className="absolute bottom-0 -left-10 w-40 h-40 rounded-full bg-purple-500/20 blur-3xl -z-10"></div>
          </motion.div>
        </div>
        
        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-20 text-center"
        >
          <h3 className="text-2xl font-bold text-white mb-4">Bereit für die Zukunft der Holografie?</h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Werden Sie Teil der Holografie-Revolution und erhalten Sie exklusiven Zugang zur IHoloTool Beta.
          </p>
          <a
            href="#preorder"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary to-purple-600 text-white font-medium rounded-full hover:opacity-90 transition-opacity shadow-lg hover:shadow-primary/30"
          >
            Jetzt vorbestellen
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Demo;
